import React, { useEffect, useRef, useState } from 'react';
import MappingIntake from './MappingIntake';
import WhitespacePanel from './WhitespacePanel';
import {
  getModelStatus, requestModel, validateSales, commitSales,
  getBrandModel, trainBrandModel, predictSite, getAccount, CATEGORIES,
} from '../api';

const fmtR = (n) => {
  if (n == null) return '—';
  if (Math.abs(n) >= 1e6) return `R ${(n / 1e6).toFixed(2)}m`;
  if (Math.abs(n) >= 1e3) return `R ${Math.round(n / 1e3)}k`;
  return `R ${Math.round(n)}`;
};
const DRIVER_LABEL = {
  log_rep: 'Catchment spend', log_hh: 'Households', target_lsm_share: 'Income fit',
  visibility: 'Visibility', competitor_count: 'Competition', pop_density: 'Population density',
  drive_thru: 'Drive-thru', store_age_years: 'Store maturity', log_floor_area: 'Floor area',
};
const driverName = (f) => DRIVER_LABEL[f] || (f.startsWith('format=') ? `Format · ${f.slice(7)}` : f);

const REQUIREMENTS = [
  { key: 'store_ref', req: true, label: 'Store reference', note: 'Your internal store code' },
  { key: 'coords', req: true, label: 'Latitude & longitude', note: 'Or a full address we can geocode' },
  { key: 'avg_weekly_sales', req: true, label: 'Average weekly sales', note: 'The target — Rand value per store' },
  { key: 'open_date', req: false, label: 'Open date', note: 'Store maturity — a top predictor' },
  { key: 'drive_thru', req: false, label: 'Drive-thru (y/n)', note: 'Biggest QSR driver we can’t see' },
  { key: 'format', req: false, label: 'Format', note: 'Mall / high-street / forecourt / taxi-rank' },
  { key: 'floor_area_m2', req: false, label: 'Floor area (m²)', note: 'Lets us normalise trading density' },
  { key: 'period_year', req: false, label: 'Year', note: 'Include to send multi-year (panel) data' },
];

const TEMPLATE_HEADERS = [
  'store_ref', 'latitude', 'longitude', 'address', 'avg_weekly_sales',
  'open_date', 'drive_thru', 'format', 'floor_area_m2', 'period_year',
];
const TEMPLATE_EXAMPLE = [
  'JHB-0142', '-26.0124', '28.0089', 'Cnr Witkoppen & Cedar, Fourways',
  '185000', '2019-04-01', 'yes', 'drive-thru', '210', '2025',
];

const STATUS_STEPS = ['requested', 'uploaded', 'in_review', 'training', 'live'];
const STATUS_LABEL = {
  requested: 'Requested', uploaded: 'Data uploaded', in_review: 'Under review',
  training: 'Training', live: 'Model live',
};

function downloadTemplate() {
  const csv = TEMPLATE_HEADERS.join(',') + '\n' +
    TEMPLATE_EXAMPLE.map(v => (v.includes(',') ? `"${v}"` : v)).join(',') + '\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'siteiq-sales-template.csv'; a.click();
  URL.revokeObjectURL(url);
}

export default function PredictivePanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [report, setReport] = useState(null);   // validation report
  const [csvText, setCsvText] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);

  const [mapping, setMapping] = useState(false);
  const [category, setCategory] = useState('qsr');
  const [brandVerified, setBrandVerified] = useState(false);
  const [model, setModel] = useState(null);
  const [training, setTraining] = useState(false);
  const [pred, setPred] = useState({ lat: '', lon: '', drive_thru: true, format: 'drive-thru', floor_area_m2: '', store_age_years: '3' });
  const [predResult, setPredResult] = useState(null);
  const [predicting, setPredicting] = useState(false);

  const load = () => {
    getModelStatus().then(setStatus).catch(() => setStatus(null)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    getAccount().then(acc => {
      setBrandVerified(!!acc?.brand_verified);
      const cats = acc?.retail_categories;
      if (cats?.length) setCategory(cats[0]);
    }).catch(() => {});
  }, []);

  const loadModel = () => {
    getBrandModel(category).then(setModel).catch(() => setModel(null));
  };
  useEffect(loadModel, [category]);

  const onTrain = async () => {
    setTraining(true); setMsg(null);
    try {
      const r = await trainBrandModel(category);
      setMsg(r.deduped
        ? 'A training job is already in progress for this brand.'
        : 'Training queued — a worker will process it shortly.');
      loadModel();
    } catch (e) { setMsg(e.message || 'Could not start training'); }
    finally { setTraining(false); }
  };

  const job = model?.job;
  const jobActive = job && (job.status === 'queued' || job.status === 'running');

  const onPredict = async () => {
    setPredicting(true); setPredResult(null); setMsg(null);
    try {
      const r = await predictSite({
        lat: parseFloat(pred.lat), lon: parseFloat(pred.lon), category,
        drive_thru: pred.drive_thru,
        format: pred.format || null,
        floor_area_m2: pred.floor_area_m2 ? parseFloat(pred.floor_area_m2) : null,
        store_age_years: pred.store_age_years ? parseFloat(pred.store_age_years) : null,
      });
      setPredResult(r);
    } catch (e) { setMsg(e.message || 'Prediction failed'); }
    finally { setPredicting(false); }
  };

  const onRequest = async () => {
    setRequesting(true);
    try { await requestModel(); load(); }
    catch (e) { setMsg(e.message || 'Could not submit request'); }
    finally { setRequesting(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMsg(null); setReport(null);
    const text = await file.text();
    setCsvText(text);
    setFileName(file.name);
    setBusy(true);
    try {
      const r = await validateSales(text);
      setReport(r);
      if (!r.ok) setMsg(null);
    } catch (err) {
      setMsg(err.message || 'Validation failed');
    } finally { setBusy(false); }
  };

  const onCommit = async () => {
    if (!csvText) return;
    setBusy(true); setMsg(null);
    try {
      const res = await commitSales(csvText, fileName);
      setMsg(`Uploaded — ${res.committed} store${res.committed === 1 ? '' : 's'} saved${res.skipped ? `, ${res.skipped} skipped` : ''}.`);
      setReport(null); setCsvText(null); setFileName(null);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      setMsg(err.message || 'Upload failed');
    } finally { setBusy(false); }
  };

  const engaged = status && status.status;
  const brandMissing = !loading && status && status.brand === null;
  const stepIdx = engaged ? STATUS_STEPS.indexOf(status.status) : -1;

  return (
    <div style={{ ...styles.wrap, ...(mapping ? { maxWidth: 'none' } : {}) }}>
      <div style={styles.head}>
        <h2 style={styles.h2}>Predictive Analytics</h2>
        <p style={styles.sub}>
          Train a sales model on your real store performance to forecast revenue
          for new sites — beyond the market-share estimate.
        </p>
      </div>

      {loading && <div style={styles.muted}>Loading…</div>}

      {brandMissing && (
        <div style={styles.notice}>Set your brand in Account before requesting predictive analytics.</div>
      )}

      {/* ---- request gate ---- */}
      {!loading && !brandMissing && !engaged && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Upgrade to a calibrated sales model</div>
          <div style={styles.cardBody}>
            We train on your actual store sales to predict weekly revenue for any
            new location, with confidence intervals and network benchmarking. It’s
            a guided engagement — request it, share your data, and we build and
            validate the model before any prediction goes live.
          </div>
          <button className="sq-btn primary" onClick={onRequest} disabled={requesting} style={{ marginTop: 4 }}>
            {requesting ? 'Submitting…' : 'Request predictive analytics'}
          </button>
        </div>
      )}

      {/* ---- status tracker ---- */}
      {engaged && !mapping && (
        <div style={styles.tracker}>
          {STATUS_STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div style={styles.step}>
                <div style={{ ...styles.dot, ...(i <= stepIdx ? styles.dotOn : {}) }} />
                <span style={{ ...styles.stepLabel, ...(i === stepIdx ? { color: 'var(--s-text)' } : {}) }}>
                  {STATUS_LABEL[s]}
                </span>
              </div>
              {i < STATUS_STEPS.length - 1 && <div style={{ ...styles.line, ...(i < stepIdx ? styles.lineOn : {}) }} />}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* ---- verification gate ---- */}
      {engaged && !mapping && !brandVerified && (
        <div style={styles.notice}>
          Your brand is pending verification. You can prepare your data and download the
          template now — uploads and model access unlock once we confirm brand ownership.
        </div>
      )}

      {/* ---- requirements + template ---- */}
      {engaged && !mapping && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>What we need</div>
          <div style={styles.reqList}>
            {REQUIREMENTS.map(r => (
              <div key={r.key} style={styles.reqRow}>
                <span style={{ ...styles.badge, ...(r.req ? styles.badgeReq : styles.badgeOpt) }}>
                  {r.req ? 'required' : 'optional'}
                </span>
                <span style={styles.reqLabel}>{r.label}</span>
                <span style={styles.reqNote}>{r.note}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
            <button className="sq-btn" onClick={downloadTemplate}>↓ Download CSV template</button>
            <span style={styles.hint}>One row per store. Add a <code style={styles.code}>year</code> column for multi-year data.</span>
          </div>
        </div>
      )}

      {/* ---- upload (schema-agnostic mapping) ---- */}
      {engaged && (
        <div style={styles.card}>
          {mapping ? (
            <MappingIntake
              onCancel={() => setMapping(false)}
              onDone={() => { setMapping(false); load(); }}
            />
          ) : (
            <>
              <div style={styles.cardTitle}>Upload sales data</div>
              <div style={styles.cardBody}>
                Upload any store + sales export — no template. We detect your columns,
                you confirm the mapping once, and we reuse it next time. Raw figures are
                never exposed; only the derived model is used.
              </div>
              <button
                className="sq-btn primary"
                onClick={() => setMapping(true)}
                disabled={!brandVerified}
                title={!brandVerified ? 'Available once your brand is verified' : undefined}
              >
                {brandVerified ? 'Upload & map data' : 'Upload (pending verification)'}
              </button>
              {msg && <div style={styles.msg}>{msg}</div>}
            </>
          )}
        </div>
      )}

      {/* ---- model & prediction ---- */}
      {engaged && !mapping && status.stores_uploaded > 0 && (
        <div style={styles.card}>
          <div style={styles.modelHead}>
            <div style={styles.cardTitle}>Sales model</div>
            <select className="sq-select" value={category} onChange={e => setCategory(e.target.value)} style={{ marginBottom: 12 }}>
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>

          {model?.trained ? (
            <>
              <div style={styles.metricRow}>
                <div style={styles.metric}><div style={styles.metricV}>{model.n_stores}</div><div style={styles.metricK}>stores</div></div>
                <div style={styles.metric}><div style={styles.metricV}>{model.mape_cv != null ? `${(model.mape_cv * 100).toFixed(1)}%` : '—'}</div><div style={styles.metricK}>CV MAPE</div></div>
                <div style={styles.metric}><div style={styles.metricV}>{model.r2_cv != null ? model.r2_cv.toFixed(2) : '—'}</div><div style={styles.metricK}>CV R²</div></div>
                <div style={styles.metric}><div style={styles.metricV}>v{model.version}</div><div style={styles.metricK}>version</div></div>
              </div>

              {model.report && (
                <div style={styles.report}>
                  <div style={styles.reportModel}>
                    {model.report.model}
                    <span style={{ ...styles.rBadge, ...(model.report.quality_ok ? styles.rBadgeOk : styles.rBadgeHold) }}>
                      {model.report.quality_ok ? 'live' : 'in review'}
                    </span>
                  </div>
                  <div style={styles.reportWhy}>{model.report.why}</div>
                  <div style={styles.candHead}>{model.report.backtest?.method}</div>
                  {(model.report.candidates || []).map(c => (
                    <div key={c.name} style={styles.candRow}>
                      <span style={{ color: c.champion ? 'var(--s-text)' : 'var(--s-muted)', fontWeight: c.champion ? 600 : 400 }}>
                        {c.champion ? '● ' : '○ '}{c.name}
                      </span>
                      <span style={styles.candNum}>MAPE {(c.mape * 100).toFixed(1)}%</span>
                      <span style={styles.candNum}>R² {c.r2.toFixed(2)}</span>
                    </div>
                  ))}
                  <div style={styles.reportFoot}>
                    80% interval covered {(model.report.backtest?.interval_coverage * 100).toFixed(0)}% of held-out
                    stores · {model.report.backtest?.n_stores} stores across {model.report.backtest?.n_metros} metros
                  </div>
                </div>
              )}

              {model.top_drivers?.length > 0 && (
                <div style={styles.drivers}>
                  <div style={styles.driversLabel}>Top drivers</div>
                  {model.top_drivers.slice(0, 5).map(d => (
                    <div key={d.feature} style={styles.driverRow}>
                      <span style={styles.driverName}>{driverName(d.feature)}</span>
                      <span style={styles.driverBar}>
                        <span style={{
                          display: 'block', height: '100%', borderRadius: 99,
                          width: `${Math.min(100, Math.abs(d.coef) / Math.abs(model.top_drivers[0].coef) * 100)}%`,
                          background: d.coef >= 0 ? 'var(--s-green)' : 'var(--s-red)',
                        }} />
                      </span>
                      <span style={{ ...styles.driverCoef, color: d.coef >= 0 ? 'var(--s-green)' : 'var(--s-red)' }}>
                        {d.coef >= 0 ? '+' : ''}{d.coef.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {brandVerified && (
                jobActive ? (
                  <div style={{ ...styles.jobNote, marginTop: 12 }}>
                    <span style={styles.spinnerDot} />Retraining {job.status}…
                  </div>
                ) : (
                  <button className="sq-btn" onClick={onTrain} disabled={training} style={{ marginTop: 12 }}>
                    {training ? 'Queuing…' : 'Retrain'}
                  </button>
                )
              )}

              {/* location-test predictor */}
              <div style={styles.predBox}>
                <div style={styles.driversLabel}>Test a location</div>
                <div style={styles.predGrid}>
                  <input className="sq-input" placeholder="Latitude" value={pred.lat} onChange={e => setPred({ ...pred, lat: e.target.value })} style={styles.predInput} />
                  <input className="sq-input" placeholder="Longitude" value={pred.lon} onChange={e => setPred({ ...pred, lon: e.target.value })} style={styles.predInput} />
                  <select className="sq-select" value={pred.format} onChange={e => setPred({ ...pred, format: e.target.value })} style={styles.predInput}>
                    <option value="drive-thru">Drive-thru</option>
                    <option value="mall">Mall</option>
                    <option value="forecourt">Forecourt</option>
                    <option value="high-street">High-street</option>
                  </select>
                  <input className="sq-input" placeholder="Floor m²" value={pred.floor_area_m2} onChange={e => setPred({ ...pred, floor_area_m2: e.target.value })} style={styles.predInput} />
                </div>
                <label style={styles.dtToggle}>
                  <input type="checkbox" checked={pred.drive_thru} onChange={e => setPred({ ...pred, drive_thru: e.target.checked })} />
                  Drive-thru
                </label>
                <button className="sq-btn primary" onClick={onPredict} disabled={predicting || !pred.lat || !pred.lon} style={{ marginTop: 10 }}>
                  {predicting ? 'Predicting…' : 'Predict weekly sales'}
                </button>
                {predResult && (
                  <div style={styles.predResult}>
                    <div style={styles.predBig}>{fmtR(predResult.predicted_weekly_sales)}<small style={styles.predSmall}> /week</small></div>
                    <div style={styles.predRange}>80% range {fmtR(predResult.low)} – {fmtR(predResult.high)} · model v{predResult.model_version}</div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={styles.cardBody}>
              {!brandVerified ? (
                'Your model is being built — we verify brand ownership and validate the model before predictions go live.'
              ) : jobActive ? (
                <div style={styles.jobNote}>
                  <span style={styles.spinnerDot} />
                  Training {job.status} — a worker is processing it. Refresh in a minute.
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 12 }}>
                    {status.stores_uploaded} stores on file. Train the {CATEGORIES.find(c => c.key === category)?.label} model
                    to forecast sales for new sites.
                  </div>
                  {job?.status === 'failed' && (
                    <div style={styles.jobErr}>Last run failed: {job.error || 'unknown error'}</div>
                  )}
                  <button className="sq-btn primary" onClick={onTrain} disabled={training}>
                    {training ? 'Queuing…' : job?.status === 'failed' ? 'Retry training' : 'Train model'}
                  </button>
                </>
              )}
            </div>
          )}
          {msg && <div style={styles.msg}>{msg}</div>}
        </div>
      )}

      {/* ---- whitespace / where to open next ---- */}
      {engaged && !mapping && model?.trained && (
        <WhitespacePanel category={category} enabled={!!model?.trained} />
      )}

      {/* ---- uploaded datasets ---- */}
      {engaged && !mapping && status.datasets?.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Uploads · {status.stores_uploaded} stores on file</div>
          {status.datasets.map(d => (
            <div key={d.id} style={styles.dsRow}>
              <span style={styles.dsName}>{d.filename || `Dataset ${d.id}`}</span>
              <span style={styles.dsMeta}>{d.valid_count}/{d.row_count} rows</span>
              <span style={styles.dsDate}>{new Date(d.uploaded_at).toLocaleDateString('en-ZA')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { padding: '26px 30px 40px', maxWidth: 720, margin: '0 auto' },
  head: { marginBottom: 20 },
  h2: { fontFamily: 'var(--s-serif)', fontSize: 24, fontWeight: 700, color: 'var(--s-text)' },
  sub: { fontSize: 13.5, color: 'var(--s-muted)', lineHeight: 1.6, marginTop: 6, maxWidth: 560 },
  muted: { fontSize: 13, color: 'var(--s-faint)', padding: '8px 0' },
  notice: { fontSize: 13, color: 'var(--s-amber)', background: 'rgba(255,176,46,0.1)', border: '1px solid rgba(255,176,46,0.25)', borderRadius: 10, padding: '12px 14px' },

  card: { border: '1px solid var(--s-line)', borderRadius: 14, padding: '18px 20px', marginBottom: 16, background: 'var(--s-w02)' },
  cardTitle: { font: '600 11px var(--s-sans)', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--s-muted)', marginBottom: 12 },
  cardBody: { fontSize: 13.5, color: 'var(--s-muted)', lineHeight: 1.7, marginBottom: 14 },

  tracker: { display: 'flex', alignItems: 'center', margin: '4px 0 20px', flexWrap: 'wrap' },
  step: { display: 'flex', alignItems: 'center', gap: 8 },
  dot: { width: 10, height: 10, borderRadius: '50%', background: 'var(--s-w14)', flexShrink: 0 },
  dotOn: { background: 'var(--s-accent)' },
  stepLabel: { font: '600 11px var(--s-sans)', color: 'var(--s-faint)', whiteSpace: 'nowrap' },
  line: { width: 26, height: 1, background: 'var(--s-w14)', margin: '0 8px' },
  lineOn: { background: 'var(--s-accent)' },

  reqList: { display: 'flex', flexDirection: 'column', gap: 2 },
  reqRow: { display: 'grid', gridTemplateColumns: '78px 150px 1fr', alignItems: 'center', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--s-line2)' },
  badge: { font: '600 9px var(--s-sans)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 99, textAlign: 'center' },
  badgeReq: { background: 'rgba(255,106,61,0.14)', color: 'var(--s-accent)' },
  badgeOpt: { background: 'var(--s-w06)', color: 'var(--s-faint)' },
  reqLabel: { fontSize: 13, fontWeight: 600, color: 'var(--s-text)' },
  reqNote: { fontSize: 12, color: 'var(--s-faint)' },

  hint: { fontSize: 11.5, color: 'var(--s-faint)', lineHeight: 1.6 },
  code: { fontFamily: 'var(--s-mono)', background: 'var(--s-w08)', padding: '1px 5px', borderRadius: 4, fontSize: 11 },

  file: { display: 'block', fontSize: 13, color: 'var(--s-muted)', marginBottom: 10, width: '100%' },
  errBox: { fontSize: 12.5, color: 'var(--s-red)', background: 'rgba(255,93,93,0.08)', border: '1px solid rgba(255,93,93,0.25)', borderRadius: 10, padding: '11px 13px', marginTop: 10 },
  validBox: { marginTop: 12 },
  validHead: { fontSize: 13.5, color: 'var(--s-muted)', marginBottom: 10 },
  previewWrap: { overflowX: 'auto', border: '1px solid var(--s-line2)', borderRadius: 10 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '8px 10px', font: '600 9.5px var(--s-sans)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--s-faint)', borderBottom: '1px solid var(--s-line)' },
  td: { padding: '7px 10px', color: 'var(--s-text)', borderBottom: '1px solid var(--s-line2)', fontFamily: 'var(--s-mono)', fontSize: 11.5, whiteSpace: 'nowrap' },
  errors: { marginTop: 10 },
  errSummary: { fontSize: 12, color: 'var(--s-amber)', cursor: 'pointer' },
  errLine: { fontSize: 11.5, color: 'var(--s-faint)', fontFamily: 'var(--s-mono)', padding: '3px 0 3px 12px' },
  msg: { fontSize: 12.5, color: 'var(--s-green)', marginTop: 12 },

  jobNote: { display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--s-amber)' },
  spinnerDot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--s-amber)', flexShrink: 0, animation: 'pulse 1.4s ease infinite' },
  jobErr: { fontSize: 12.5, color: 'var(--s-red)', marginBottom: 10 },
  modelHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' },
  report: { border: '1px solid var(--s-line2)', borderRadius: 11, padding: '13px 15px', marginBottom: 14, background: 'var(--s-w02)' },
  reportModel: { display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: 14, color: 'var(--s-text)' },
  rBadge: { font: '600 9px var(--s-sans)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 99 },
  rBadgeOk: { background: 'rgba(61,220,132,0.13)', color: 'var(--s-green)' },
  rBadgeHold: { background: 'rgba(255,176,46,0.13)', color: 'var(--s-amber)' },
  reportWhy: { fontSize: 12.5, color: 'var(--s-muted)', lineHeight: 1.6, margin: '7px 0 12px' },
  candHead: { font: '600 10px var(--s-sans)', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--s-faint)', marginBottom: 6 },
  candRow: { display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 14, alignItems: 'center', padding: '4px 0', fontSize: 12.5 },
  candNum: { font: '500 11.5px var(--s-mono)', color: 'var(--s-muted)', whiteSpace: 'nowrap' },
  reportFoot: { fontSize: 11, color: 'var(--s-faint)', lineHeight: 1.5, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--s-line2)' },
  metricRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 },
  metric: { border: '1px solid var(--s-line2)', borderRadius: 10, padding: '11px 12px', background: 'var(--s-w025)', textAlign: 'center' },
  metricV: { font: '700 18px var(--s-mono)', color: 'var(--s-text)', letterSpacing: '-0.02em' },
  metricK: { fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--s-faint)', marginTop: 3 },
  drivers: { marginTop: 4 },
  driversLabel: { font: '600 10px var(--s-sans)', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--s-faint)', marginBottom: 8 },
  driverRow: { display: 'grid', gridTemplateColumns: '120px 1fr 46px', alignItems: 'center', gap: 10, padding: '3px 0' },
  driverName: { fontSize: 12.5, color: 'var(--s-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  driverBar: { height: 5, borderRadius: 99, background: 'var(--s-w07)', overflow: 'hidden' },
  driverCoef: { font: '600 11.5px var(--s-mono)', textAlign: 'right' },
  predBox: { marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--s-line2)' },
  predGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  predInput: { padding: '8px 11px', borderRadius: 9, border: '1px solid var(--s-line)', background: 'var(--s-w05)', color: 'var(--s-text)', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
  dtToggle: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, color: 'var(--s-muted)', cursor: 'pointer' },
  predResult: { marginTop: 14, padding: '13px 15px', border: '1px solid rgba(255,106,61,0.25)', borderRadius: 11, background: 'rgba(255,106,61,0.06)' },
  predBig: { font: '700 24px var(--s-mono)', color: 'var(--s-text)', letterSpacing: '-0.03em' },
  predSmall: { fontSize: 12, color: 'var(--s-muted)', fontWeight: 500 },
  predRange: { fontSize: 12, color: 'var(--s-muted)', marginTop: 4, fontFamily: 'var(--s-mono)' },

  dsRow: { display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--s-line2)' },
  dsName: { fontSize: 13, color: 'var(--s-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  dsMeta: { font: '600 12px var(--s-mono)', color: 'var(--s-muted)' },
  dsDate: { fontSize: 11.5, color: 'var(--s-faint)', fontFamily: 'var(--s-mono)' },
};
